import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-hot-toast'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { I, Icon } from '../shared/ns-icons'
import RichTextEditor from '../shared/RichTextEditor'
import '../../styles/admin-pages.css'

/* WebNest — Admin Help Center
   Manages the knowledge base that powers the client Help page (/client/help):
   articles, categories, FAQs and the public status board. Backed by /api/wiki —
   every publish/edit here is what clients read. UI ported 1:1 from the prototype. */

const HC_STATUS_OPTS = [
  { v: 'operational', label: 'Operational', tone: 'success' },
  { v: 'degraded', label: 'Degraded', tone: 'warning' },
  { v: 'down', label: 'Outage', tone: 'danger' },
]
const HC_STATUS_TONE = { operational: 'success', degraded: 'warning', down: 'danger' }
const ARTICLE_STATUS_TONE = { published: 'success', draft: 'warning', archived: '' }

// "3d ago" style relative time from a SQL timestamp.
function timeAgo(ts) {
  if (!ts) return '—'
  const then = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z').getTime()
  if (Number.isNaN(then)) return '—'
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`
  const w = Math.floor(d / 7); if (w < 5) return `${w}w ago`
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(d / 365)}y ago`
}

// Map our editable FAQ shape (uses `on`) back to the DB/API shape (`is_visible`).
const faqsToDb = (list) => list.map(({ on, ...f }) => ({ ...f, is_visible: on ? 1 : 0 }))

// Article bodies are stored as HTML. Older/seeded articles may still be plain
// text (paragraphs split on blank lines, `# ` = heading) — convert those so the
// rich editor shows them formatted. Anything already containing tags is left as-is.
const bodyIsHtml = (b) => /<[a-z][\s\S]*>/i.test(b || '')
const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
function toEditableHtml(body) {
  if (!body) return ''
  if (bodyIsHtml(body)) return body
  return body.split(/\n{2,}/).map(p => {
    p = p.trim()
    if (!p) return ''
    return p.startsWith('# ') ? `<h2>${escapeHtml(p.slice(2))}</h2>` : `<p>${escapeHtml(p)}</p>`
  }).join('')
}

/* ---- Mini stat card (ported from admin-pages prototype) ---- */
function MiniStat({ label, value, icon, tone }) {
  return (
    <div className="card card-pad-sm" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <span className={`icon-tile ${tone || ''}`}><Icon name={icon} /></span>
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{value}</div>
      </div>
    </div>
  )
}

/* ============================ PAGE ============================ */
function AdminHelp() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('articles')
  const [editing, setEditing] = useState(null) // article object or {} for new
  const [editingCat, setEditingCat] = useState(null) // category object or {} for new

  // Backend is the source of truth; we mirror it locally for a snappy UI AND keep
  // the react-query cache in sync on every change (writeCache) so navigating away
  // and back never shows stale data. staleTime 0 is a final safety net.
  const { data, isLoading } = useQuery('wiki-admin', () => apiService.get('/wiki/admin').then(r => r.data), { staleTime: 0 })
  const [articles, setArticles] = useState([])
  const [faqs, setFaqs] = useState([])
  const [status, setStatus] = useState([])
  const [categories, setCategories] = useState([])
  const [banner, setBanner] = useState(null)

  useEffect(() => {
    if (!data) return
    setArticles(data.articles || [])
    setFaqs((data.faqs || []).map(f => ({ ...f, on: !!f.is_visible })))
    setStatus(data.status || [])
    setCategories(data.categories || [])
    setBanner(data.banner || null)
  }, [data])

  // Keep the cached admin payload identical to what's on screen.
  const writeCache = (partial) => queryClient.setQueryData('wiki-admin', (o) => o ? { ...o, ...partial } : o)
  // The client Help page / preview read a separate 'wiki' query — refetch it.
  const touchClient = () => queryClient.invalidateQueries('wiki')
  // Pull a clean copy from the server if an optimistic update failed.
  const resync = () => queryClient.invalidateQueries('wiki-admin')

  // Setters that update both local state and the cache (passed to child tabs).
  const setArticlesBoth = (next) => { setArticles(next); writeCache({ articles: next }) }
  const setStatusBoth = (updater) => setStatus(prev => { const next = typeof updater === 'function' ? updater(prev) : updater; writeCache({ status: next }); return next })
  const setFaqsBoth = (updater) => setFaqs(prev => { const next = typeof updater === 'function' ? updater(prev) : updater; writeCache({ faqs: faqsToDb(next) }); return next })

  const catName = (id) => (categories.find(c => c.id === id) || {}).name || 'Uncategorised'

  const saveCategory = async (draft) => {
    try {
      if (draft._isNew) {
        const created = await apiService.post('/wiki/categories', { name: draft.name, icon: draft.icon, description: draft.description }).then(r => r.data)
        setCategories(prev => { const next = [...prev, created]; writeCache({ categories: next }); return next })
        toast.success('Category added')
      } else {
        const saved = await apiService.put(`/wiki/categories/${draft.id}`, { name: draft.name, icon: draft.icon, description: draft.description }).then(r => r.data)
        setCategories(prev => { const next = prev.map(c => c.id === saved.id ? saved : c); writeCache({ categories: next }); return next })
        toast.success('Category updated')
      }
      setEditingCat(null)
      touchClient()
    } catch (e) {
      toast.error('Could not save category')
    }
  }

  const deleteCategory = async (id) => {
    // Server detaches articles from the category; mirror that locally too.
    setCategories(prev => { const next = prev.filter(c => c.id !== id); writeCache({ categories: next }); return next })
    setArticlesBoth(articles.map(a => a.category_id === id ? { ...a, category_id: null } : a))
    setEditingCat(null)
    try { await apiService.delete(`/wiki/categories/${id}`); toast.success('Category deleted'); touchClient() }
    catch { toast.error('Could not delete category'); resync() }
  }

  const saveBanner = async (payload) => {
    try {
      const saved = await apiService.put('/wiki/banner', payload).then(r => r.data)
      setBanner(saved); writeCache({ banner: saved })
      toast.success('Update posted to all clients')
      touchClient()
    } catch { toast.error('Could not post update') }
  }
  const clearBanner = async () => {
    try { await apiService.delete('/wiki/banner'); setBanner(null); writeCache({ banner: null }); toast.success('Banner cleared'); touchClient() }
    catch { toast.error('Could not clear banner') }
  }

  const published = articles.filter(a => a.status === 'published')
  const drafts = articles.filter(a => a.status === 'draft')
  // Real rolling 30-day article opens (server-computed from the view log).
  const views30d = data?.views30d || 0
  const views30dLabel = views30d >= 1000 ? (views30d / 1000).toFixed(1) + 'k' : String(views30d)

  const saveArticle = async (draft) => {
    try {
      const payload = { title: draft.title, category_id: draft.category_id, status: draft.status, body: draft.body, author: draft.author }
      if (draft._isNew) {
        const saved = await apiService.post('/wiki/articles', payload).then(r => r.data)
        setArticlesBoth([saved, ...articles])
      } else {
        const saved = await apiService.put(`/wiki/articles/${draft.id}`, payload).then(r => r.data)
        setArticlesBoth(articles.map(a => a.id === saved.id ? saved : a))
      }
      setEditing(null)
      toast.success(draft.status === 'published' ? 'Article published — live on the client Help page' : 'Draft saved')
      touchClient()
    } catch (e) {
      toast.error('Could not save article')
    }
  }

  const removeArticle = async (id) => {
    setArticlesBoth(articles.filter(a => a.id !== id))
    try { await apiService.delete(`/wiki/articles/${id}`); toast.success('Article deleted'); touchClient() }
    catch { toast.error('Could not delete article'); resync() }
  }

  const toggleArticle = async (a) => {
    const next = a.status === 'published' ? 'draft' : 'published'
    setArticlesBoth(articles.map(x => x.id === a.id ? { ...x, status: next } : x))
    try {
      const saved = await apiService.put(`/wiki/articles/${a.id}`, { status: next }).then(r => r.data)
      setArticles(prev => { const n = prev.map(x => x.id === saved.id ? saved : x); writeCache({ articles: n }); return n })
      toast.success(a.status === 'published' ? 'Article unpublished' : 'Article published — live now')
      touchClient()
    } catch { toast.error('Could not update article'); resync() }
  }

  if (isLoading) return <div style={{ padding: 60 }}><LoadingSpinner /></div>

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Help Center</div>
          <h1>Help Center</h1>
          <div className="subtitle">{published.length} published · {drafts.length} drafts · powers the client Help &amp; support page</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn" onClick={() => navigate('/admin/help/preview')}><I.eye /> Preview client view</button>
          <button className="btn btn-primary" onClick={() => setEditing({ _isNew: true, id: null, title: '', category_id: categories[0]?.id || 'getting-started', status: 'draft', body: '', author: 'SV' })}><I.plus /> New article</button>
        </div>
      </div>

      <div className="page-body">
        {/* Stats */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <MiniStat label="Published" value={published.length} icon="file" tone="success" />
          <MiniStat label="Drafts" value={drafts.length} icon="edit" tone="warning" />
          <MiniStat label="Categories" value={categories.length} icon="layers" tone="accent" />
          <MiniStat label="Article views · 30d" value={views30dLabel} icon="eye" />
        </div>

        {/* Tabs */}
        <div className="tabs" style={{ alignSelf: 'flex-start' }}>
          {[['articles', `Articles · ${articles.length}`], ['categories', `Categories · ${categories.length}`], ['faqs', `FAQs · ${faqs.length}`], ['status', 'Status board']].map(([k, l]) => (
            <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>

        {tab === 'articles' && <HCArticles articles={articles} catName={catName} onEdit={setEditing} onDelete={removeArticle} onToggle={toggleArticle} />}
        {tab === 'categories' && <HCCategories categories={categories} articles={articles} onAdd={() => setEditingCat({ _isNew: true, name: '', icon: 'layers', description: '' })} onRename={setEditingCat} onManage={() => setTab('articles')} />}
        {tab === 'faqs' && <HCFaqs faqs={faqs} setFaqs={setFaqsBoth} touchClient={touchClient} resync={resync} />}
        {tab === 'status' && <HCStatus status={status} setStatus={setStatusBoth} banner={banner} onSaveBanner={saveBanner} onClearBanner={clearBanner} touchClient={touchClient} resync={resync} />}
      </div>

      {editing && <HCEditor key={editing.id || 'new'} article={editing} categories={categories} catName={catName} onClose={() => setEditing(null)} onSave={saveArticle} />}
      {editingCat && <HCCategoryModal key={editingCat.id || 'new-cat'} category={editingCat} onClose={() => setEditingCat(null)} onSave={saveCategory} onDelete={deleteCategory} />}
    </>
  )
}

/* ============================ ARTICLES ============================ */
function HCArticles({ articles, catName, onEdit, onDelete, onToggle }) {
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState('all')

  const counts = {
    all: articles.length,
    published: articles.filter(a => a.status === 'published').length,
    draft: articles.filter(a => a.status === 'draft').length,
    archived: articles.filter(a => a.status === 'archived').length,
  }
  const rows = articles.filter(a =>
    (filter === 'all' || a.status === filter) &&
    (q === '' || (a.title || '').toLowerCase().includes(q.toLowerCase()))
  )

  return (
    <>
      <div className="filter-bar">
        <I.search style={{ color: 'var(--text-muted)', fontSize: 16 }} />
        <input className="input" placeholder="Search articles by title…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="vdiv" style={{ height: 22 }} />
        <div className="segmented">
          {[['all', 'All'], ['published', 'Published'], ['draft', 'Drafts'], ['archived', 'Archived']].map(([k, l]) => (
            <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>
              {l} <span style={{ color: 'var(--text-faint)', marginLeft: 4, fontFamily: 'var(--font-mono)' }}>{counts[k]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="sites-table">
        <div className="row header" style={{ gridTemplateColumns: '2.6fr 1.2fr 1fr 0.8fr 0.9fr 1fr auto' }}>
          <div>Article</div><div>Category</div><div>Status</div><div>Views</div><div>Helpful</div><div>Updated</div><div></div>
        </div>
        {rows.map(a => (
          <div key={a.id} className="row" style={{ gridTemplateColumns: '2.6fr 1.2fr 1fr 0.8fr 0.9fr 1fr auto', cursor: 'pointer' }} onClick={() => onEdit(a)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
              <span className="icon-tile" style={{ width: 30, height: 30, fontSize: 14, flexShrink: 0 }}><I.file /></span>
              <span className="truncate" style={{ color: 'var(--text)', fontWeight: 500 }}>{a.title || 'Untitled article'}</span>
            </div>
            <div style={{ color: 'var(--text-soft)', fontSize: 12.5 }}>{catName(a.category_id)}</div>
            <div><span className={`badge badge-${ARTICLE_STATUS_TONE[a.status]}`} style={{ textTransform: 'capitalize' }}>{a.status !== 'archived' && <span className="badge-dot" />} {a.status}</span></div>
            <div style={{ fontFamily: 'var(--font-mono)', color: a.views ? 'var(--text)' : 'var(--text-faint)' }}>{a.views ? a.views.toLocaleString() : '—'}</div>
            <div style={{ fontFamily: 'var(--font-mono)', color: a.helpful == null ? 'var(--text-faint)' : 'var(--text-soft)' }}>{a.helpful == null ? '—' : a.helpful + '%'}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{timeAgo(a.updated_at)}</div>
            <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
              <button className="site-action" title={a.status === 'published' ? 'Unpublish' : 'Publish'} onClick={() => onToggle(a)}>{a.status === 'published' ? <I.eye /> : <I.upload />}</button>
              <button className="site-action" title="Edit" onClick={() => onEdit(a)}><I.edit /></button>
              <button className="site-action" title="Delete" onClick={() => onDelete(a.id)}><I.trash /></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No articles match your filters.</div>
        )}
      </div>
    </>
  )
}

/* ============================ CATEGORIES ============================ */
function HCCategories({ categories, articles, onAdd, onRename, onManage }) {
  return (
    <div className="bento" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
      {categories.map(c => {
        const n = articles.filter(a => a.category_id === c.id && a.status === 'published').length
        return (
          <div key={c.id} className="card card-pad" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <span className="icon-tile accent" style={{ width: 42, height: 42, fontSize: 19, flexShrink: 0 }}><Icon name={c.icon} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{c.name}</div>
                <span className="badge"><span style={{ fontFamily: 'var(--font-mono)' }}>{n}</span>&nbsp;live</span>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>{c.description}</div>
              <div className="flex gap-2" style={{ marginTop: 14 }}>
                <button className="btn btn-sm" onClick={() => onRename(c)}><I.edit /> Rename</button>
                <button className="btn btn-sm" onClick={() => onManage(c)}><I.list /> Manage articles</button>
              </div>
            </div>
          </div>
        )
      })}
      <button className="card card-pad hc-add-cat" onClick={onAdd}>
        <I.plus /> <span>Add category</span>
      </button>
    </div>
  )
}

/* Icon choices offered when creating / renaming a category. */
const HC_CAT_ICONS = ['rocket', 'edit', 'domain', 'card', 'layers', 'globe', 'image', 'shield', 'spark', 'bolt', 'cloud', 'help']

function HCCategoryModal({ category, onClose, onSave, onDelete }) {
  const [draft, setDraft] = useState({ name: '', icon: 'layers', description: '', ...category })
  const [saving, setSaving] = useState(false)
  const isNew = !!category._isNew
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }))

  const submit = async () => {
    if (!draft.name.trim()) return
    setSaving(true)
    await onSave(draft)
    setSaving(false)
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="card card-pad" onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 92vw)', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="flex items-center justify-between">
          <h2 className="h2">{isNew ? 'Add category' : 'Edit category'}</h2>
          <button className="site-action" onClick={onClose}><I.x /></button>
        </div>

        <div>
          <label className="hc-label">Name</label>
          <input className="input" placeholder="e.g. Security & privacy" value={draft.name} onChange={(e) => set('name', e.target.value)} autoFocus />
        </div>

        <div>
          <label className="hc-label">Description</label>
          <input className="input" placeholder="Short summary shown under the category" value={draft.description || ''} onChange={(e) => set('description', e.target.value)} />
        </div>

        <div>
          <label className="hc-label">Icon</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {HC_CAT_ICONS.map(name => (
              <button
                key={name}
                className={`icon-tile ${draft.icon === name ? 'accent' : ''}`}
                onClick={() => set('icon', name)}
                title={name}
                style={{ width: 38, height: 38, fontSize: 17, cursor: 'pointer', border: draft.icon === name ? '1px solid var(--border-accent)' : '1px solid var(--border)' }}
              ><Icon name={name} /></button>
            ))}
          </div>
        </div>

        <div className="flex items-center" style={{ gap: 10, marginTop: 4 }}>
          {!isNew && (
            <button className="btn btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => onDelete(draft.id)}><I.trash /> Delete</button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving || !draft.name.trim()} onClick={submit}><I.check /> {isNew ? 'Add category' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

/* ============================ FAQs ============================ */
function HCFaqs({ faqs, setFaqs, touchClient, resync }) {
  const [open, setOpen] = useState(-1)

  const toggle = async (f) => {
    const on = !f.on
    setFaqs(prev => prev.map(x => x.id === f.id ? { ...x, on } : x))
    try { await apiService.put(`/wiki/faqs/${f.id}`, { is_visible: on }); touchClient() }
    catch { toast.error('Could not update FAQ'); resync() }
  }

  const remove = async (id) => {
    setFaqs(prev => prev.filter(f => f.id !== id))
    try { await apiService.delete(`/wiki/faqs/${id}`); toast.success('FAQ removed'); touchClient() }
    catch { toast.error('Could not remove FAQ'); resync() }
  }

  const add = async () => {
    try {
      const created = await apiService.post('/wiki/faqs', { question: 'New question', answer: 'Write the answer here.', is_visible: false }).then(r => r.data)
      setFaqs(prev => [...prev, { ...created, on: !!created.is_visible }])
      setOpen(faqs.length)
      toast.success('FAQ added — toggle on to publish')
    } catch { toast.error('Could not add FAQ') }
  }

  const save = async (f) => {
    try { await apiService.put(`/wiki/faqs/${f.id}`, { question: f.question, answer: f.answer }); setOpen(-1); toast.success('FAQ saved'); touchClient() }
    catch { toast.error('Could not save FAQ') }
  }

  return (
    <div className="card card-pad">
      <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="h2">Frequently asked</h2>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{faqs.filter(f => f.on).length} shown on the client Help page</div>
        </div>
        <button className="btn btn-sm btn-primary" onClick={add}><I.plus /> Add FAQ</button>
      </div>
      <div>
        {faqs.map((f, i) => (
          <div key={f.id} className="hc-faq-row">
            <div className="hc-faq-head">
              <I.more className="hc-grip" />
              <button className="hc-faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <I.help style={{ color: 'var(--accent-3)', fontSize: 15, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{f.question}</span>
                <I.chevron style={{ color: 'var(--text-muted)', transform: open === i ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }} />
              </button>
              <span className={`badge badge-${f.on ? 'success' : ''}`} style={{ flexShrink: 0 }}>{f.on ? 'Live' : 'Hidden'}</span>
              <button className={`hc-switch ${f.on ? 'on' : ''}`} onClick={() => toggle(f)} title={f.on ? 'Hide from clients' : 'Show to clients'}><span /></button>
              <button className="site-action" title="Delete" onClick={() => remove(f.id)}><I.trash /></button>
            </div>
            {open === i && (
              <div className="hc-faq-edit">
                <label className="hc-label">Question</label>
                <input className="input" value={f.question} onChange={(e) => setFaqs(prev => prev.map(x => x.id === f.id ? { ...x, question: e.target.value } : x))} />
                <label className="hc-label" style={{ marginTop: 12 }}>Answer</label>
                <textarea className="input" style={{ minHeight: 90, resize: 'vertical', lineHeight: 1.55 }} value={f.answer} onChange={(e) => setFaqs(prev => prev.map(x => x.id === f.id ? { ...x, answer: e.target.value } : x))} />
                <div className="flex justify-end" style={{ marginTop: 10 }}>
                  <button className="btn btn-sm btn-primary" onClick={() => save(f)}><I.check /> Save</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ============================ STATUS BOARD ============================ */
function HCStatus({ status, setStatus, banner, onSaveBanner, onClearBanner, touchClient, resync }) {
  const allOk = status.every(s => s.state === 'operational')
  const [headline, setHeadline] = useState(banner?.headline || '')
  const [message, setMessage] = useState(banner?.message || '')

  useEffect(() => { setHeadline(banner?.headline || ''); setMessage(banner?.message || '') }, [banner])

  const setState = async (id, v) => {
    setStatus(prev => prev.map(s => s.id === id ? { ...s, state: v } : s))
    try { await apiService.put(`/wiki/status/${id}`, { state: v }); touchClient() }
    catch { toast.error('Could not update status'); resync() }
  }

  return (
    <div className="bento" style={{ gridTemplateColumns: '1.6fr 1fr', gap: 14, alignItems: 'flex-start' }}>
      <div className="card card-pad">
        <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
          <h2 className="h2">System status</h2>
          <span className={`badge badge-${allOk ? 'success' : 'warning'}`}><span className="badge-dot" /> {allOk ? 'All operational' : 'Issues reported'}</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 8 }}>Set per-service status — clients see this live on their Help page.</div>
        {status.map(s => (
          <div key={s.id} className="hc-status-row">
            <span className="dot-live" style={{ background: `var(--${HC_STATUS_TONE[s.state]})`, boxShadow: `0 0 0 4px var(--${HC_STATUS_TONE[s.state]}-soft)` }} />
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{s.label}</span>
            <div className="segmented">
              {HC_STATUS_OPTS.map(o => (
                <button key={o.v} className={s.state === o.v ? 'on' : ''} onClick={() => setState(s.id, o.v)} style={s.state === o.v ? { color: `var(--${o.tone})` } : null}>{o.label}</button>
              ))}
            </div>
          </div>
        ))}
        <div className="flex justify-end" style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={() => toast.success('Status board updated — clients notified')}><I.check /> Publish status</button>
        </div>
      </div>

      <div className="card card-pad">
        <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
          <h2 className="h2">Post an update</h2>
          {banner && <span className="badge badge-warning"><span className="badge-dot" /> Live</span>}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 12px', lineHeight: 1.5 }}>Broadcast a banner to the top of every client Help page.</div>
        <label className="hc-label">Headline</label>
        <input className="input" placeholder="e.g. Scheduled maintenance tonight" value={headline} onChange={(e) => setHeadline(e.target.value)} />
        <label className="hc-label" style={{ marginTop: 12 }}>Message</label>
        <textarea className="input" style={{ minHeight: 80, resize: 'vertical', lineHeight: 1.55 }} placeholder="What's happening, and when it'll be resolved." value={message} onChange={(e) => setMessage(e.target.value)} />
        <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} disabled={!headline.trim() && !message.trim()} onClick={() => onSaveBanner({ headline, message })}><I.send /> {banner ? 'Update banner' : 'Broadcast update'}</button>
        {banner && (
          <button className="btn" style={{ width: '100%', marginTop: 8, color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={onClearBanner}><I.x /> Clear banner</button>
        )}
      </div>
    </div>
  )
}

/* ============================ ARTICLE EDITOR DRAWER ============================ */
function HCEditor({ article, categories, catName, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({ ...article, body: toEditableHtml(article.body || '') }))
  const [saving, setSaving] = useState(false)
  const isNew = !!article._isNew
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }))
  const slug = (draft.title || 'untitled').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const submit = async (status) => {
    setSaving(true)
    await onSave({ ...draft, status })
    setSaving(false)
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span className="icon-tile accent" style={{ width: 30, height: 30, fontSize: 14 }}><I.file /></span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-faint)' }}>{isNew ? 'New article' : 'Editing'}</span>
            <span className={`badge badge-${ARTICLE_STATUS_TONE[draft.status]}`} style={{ textTransform: 'capitalize', marginLeft: 'auto' }}>{draft.status}</span>
            <button className="site-action" onClick={onClose}><I.x /></button>
          </div>
          <input className="hc-title-input" placeholder="Article title…" value={draft.title} onChange={(e) => set('title', e.target.value)} autoFocus />
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-faint)', marginTop: 6 }}>/help/{slug}</div>
        </div>

        <div className="drawer-body">
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <div className="drawer-section-label">Category</div>
              <select className="input" value={draft.category_id || ''} onChange={(e) => set('category_id', e.target.value)}>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="drawer-section-label">Status</div>
              <select className="input" value={draft.status} onChange={(e) => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div>
            <div className="drawer-section-label">Body</div>
            <RichTextEditor
              value={draft.body}
              onChange={(html) => set('body', html)}
              placeholder="Write the help article here. Use the toolbar to add headings, bold, lists and links…"
            />
          </div>

          <div className="hc-tip">
            <I.spark style={{ color: 'var(--accent-3)', flexShrink: 0, marginTop: 1 }} />
            <span>Publishing makes this article appear instantly under <strong>{catName(draft.category_id)}</strong> on every client's Help page.</span>
          </div>
        </div>

        <div className="drawer-foot" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <div style={{ flex: 1 }} />
          <button className="btn" disabled={saving} onClick={() => submit('draft')}>Save draft</button>
          <button className="btn btn-primary" disabled={saving} onClick={() => submit('published')}><I.check /> Publish</button>
        </div>
      </div>
    </div>
  )
}

export default AdminHelp
